import React, { useEffect } from 'react';

import { setCookieValue } from '@do/walrus/utils/cookie';
import _get from 'lodash/get';
import { RouteComponentProps } from 'react-router-dom';

import { useQuery } from '@apollo/react-hooks';
import useDocumentTitle from '../hooks/use-document-title';
import useSegmentTracking from '../hooks/use-segment-tracking';
import useSift from '../hooks/use-sift';
import { FormGqlData, LoginType } from '../interfaces/login';

import useOneTimeCookie from '@do/walrus/hooks/useOneTimeCookie';
import vars from '@do/walrus/scss/core/_variables.scss';
import StepTransitionManager from '@do/walrus/StepTransitionManager';
import { APP_PLATFORM_TEMPLATE_COOKIE, CHURN_SURVEY_URL } from '../constants';
import copy from '../copy';
import {
  GET_LOGIN_FORM_DATA,
  REGISTER_REFERRAL_CODE
} from '../queries/queries';
import { getRefcode } from '../utils/get-refcode';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam } from '../utils/query-utils';
import { getRandomLoginVariant } from '../utils/string-utils';
import Background from './Background';
import {
  BannerType,
  ErrorBanner,
  FormWrap,
  HeaderBanner,
  Loading,
  PaddedContents,
  Wrap
} from './Common';
import Header from './Header';
import LoginForm from './LoginForm';
import LoginSplash from './LoginSplash';
import TwoFactorAuthForm from './TwoFactorAuthForm';
import VerificationForm from './VerificationForm';

const APP_PLATFORM_TEMPLATE_URL_REGEX = /^https:\/\/cloud.digitalocean.com\/apps\/new\?(template|repo)=https:\/\/github\.com/;

const STEPS = {
  login: 0,
  two_factor_auth: 1,
  two_factor_auth_secondary: 2,
  device_verification: 3
};

const variant = getRandomLoginVariant();

const Login: React.FC<RouteComponentProps> = ({ location }) => {
  const { search } = location;

  const refcode = getRefcode(search);
  useQuery(REGISTER_REFERRAL_CODE, {
    variables: { refcode },
    skip: !refcode
  });

  const { loading, data } = useQuery<FormGqlData>(GET_LOGIN_FORM_DATA, {
    variables: {
      onboarding_origin: getQueryParam('onboarding_origin', search),
      redirect_url: getQueryParam('redirect_url', search)
    }
  });
  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const [step, setStep] = React.useState(STEPS.login);
  const [config2fa, setConfig2fa] = React.useState<LoginType>();

  useDocumentTitle(copy.form.login_title);
  useSift(_get(data, 'loginFormData.session_id'));
  useSegmentTracking('Login Variant', {
    variantType: _get(variant, 'type')
  });

  const preferredLoginState = _get(location, 'state.preferredLoginState');
  const user = _get(data, 'loginFormData.user');

  const hasPreferredLogin =
    preferredLoginState !== 'none' &&
    !loading &&
    !!_get(user, 'email') &&
    !!_get(user, 'sign_in_method') &&
    !!_get(user, 'avatar_url');

  /**
   * This handles the case of a new-to-DO user clicking on a "Deploy to DO"
   * button elsewhere on the internet.
   *
   * - The user clicks on the button and is directed to a URL like this:
   *     https://cloud.digitalocean.com/apps/new?template=https://github.com/RocketChat/Rocket.Chat/tree/master
   * - The Edge gateway detects that the user is not logged in and redirects
   *   them to the login form with a `redirect_url` param like this:
   *     https://cloud.digitalocean.com/login?redirect_url=https%3A%2F%2Fcloud.digitalocean.com%2Fapps%2Fnew%3Ftemplate%3Dhttps%3A%2F%2Fgithub.com%2FRocketChat%2FRocket.Chat%2Ftree%2Fmaster
   * - We detect that the `redirect_url` param is a "Deploy to DO" URL.
   * - We set a cookie with the complete redirect URL.
   *
   * Later on in the signup flow, once the user's account is successfully
   * activated, the onboarding app will use this cookie to redirect the user
   * back to the "Deploy to DO" create flow in the App Platform UI.
   */
  useEffect(() => {
    const redirectUrl = getQueryParam('redirect_url', search);

    if (!redirectUrl || !APP_PLATFORM_TEMPLATE_URL_REGEX.test(redirectUrl)) {
      return;
    }

    setCookieValue(APP_PLATFORM_TEMPLATE_COOKIE, redirectUrl);
  });

  if (loading) {
    return <Loading isLogin={true} />;
  }

  const googleOauthUrl = `/sessions/google${search}`;
  const githubOauthUrl = `/sessions/github${search}`;

  const oauthError = _get(data, 'loginFormData.oauth_error');
  const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
  const churnSurvey = userUUID
    ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
    : CHURN_SURVEY_URL;

  let form;
  switch (step) {
    case STEPS.login:
      form = (
        <>
          {oauthError && (
            <ErrorBanner
              error={copy.errors[oauthError] || copy.errors.unknown}
            />
          )}
          <LoginForm
            googleOauthUrl={googleOauthUrl}
            githubOauthUrl={githubOauthUrl}
            hasPreferredLogin={hasPreferredLogin}
            user={user}
            team={_get(data, 'loginFormData.team')}
            passwordSuccessBanner={_get(location, 'state.success')}
            showNext={(nextStep) => {
              if (STEPS[nextStep.type] === STEPS.two_factor_auth) {
                setConfig2fa(nextStep);
              }
              setStep(STEPS[nextStep.type]);
            }}
          />
        </>
      );
      break;
    case STEPS.two_factor_auth:
      form = config2fa && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth_secondary)}
          type={config2fa.primary}
          nextStepType={config2fa.secondary}
        />
      );

      break;
    case STEPS.two_factor_auth_secondary:
      form = config2fa && config2fa.secondary && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth)}
          type={config2fa.secondary}
          nextStepType={config2fa.primary}
          isSecondary
        />
      );
      break;
    case STEPS.device_verification:
      form = <VerificationForm goBack={() => setStep(STEPS.login)} />;
      break;
  }

  return (
    <>
      <Header isLogin={true} queryString={search} />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      <Wrap>
        <Background
          url={variant.background.image}
          horizontalPosition={variant.background.horizontalPosition}
          verticalPosition={variant.background.verticalPosition}
        />
        <FormWrap style={{ paddingBottom: `${vars.space_3}` }}>
          <StepTransitionManager currentStepNum={step}>
            <PaddedContents>{form}</PaddedContents>
          </StepTransitionManager>
        </FormWrap>

        {(variant.image || variant.title) && <LoginSplash variant={variant} />}
      </Wrap>
    </>
  );
};

export default Login;
